import React, { useState, useEffect } from 'react';
import { enhancedApi } from '../api';
import PageLayout from '../components/ui/PageLayout';
import PageHeaderActionsBar from '../components/ui/PageHeaderActionsBar';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

const AdminNotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ unread: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await enhancedApi.getNotifications();
      const data = response?.data;
      setNotifications(Array.isArray(data) ? data : (data?.results || []));
    } catch (err) {
      setError('Failed to load notifications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await enhancedApi.getNotificationStats();
      const res = response?.data;
      if (res?.overall_stats) {
        setStats({
          unread: res.overall_stats.unread || 0,
          total: res.overall_stats.total || 0
        });
      }
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      await enhancedApi.markNotificationRead(id);
      fetchNotifications();
      fetchStats();
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await enhancedApi.markAllNotificationsRead();
      fetchNotifications();
      fetchStats();
    } catch (err) {
      console.error(err);
    }
  };

  const getIcon = (category) => {
    const name = (category?.name || '').toLowerCase();
    const iconProps = { sx: { fontSize: 22, color: 'primary.main' } };
    if (name.includes('document')) return <DescriptionIcon {...iconProps} />;
    if (name.includes('message')) return <EmailIcon sx={{ ...iconProps.sx, color: 'success.main' }} />;
    if (name.includes('organizer')) return <AssignmentIcon sx={{ ...iconProps.sx, color: 'secondary.main' }} />;
    return <NotificationsIcon sx={{ ...iconProps.sx, color: 'text.secondary' }} />;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <PageLayout>
      <PageHeaderActionsBar
        title="Notifications"
        description={stats.unread > 0 ? `${stats.unread} unread` : 'All caught up'}
        primaryAction={stats.unread > 0 ? { label: 'Mark All Read', onClick: handleMarkAllRead } : null}
      />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        ) : notifications.length === 0 ? (
          <Paper sx={{ p: 6, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">No notifications yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When clients upload documents, send messages, or complete organizers, they will appear here.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {notifications.map((n) => (
              <Paper
                key={n.id}
                onClick={() => {
                  if (n.status === 'unread') handleMarkAsRead(n.id);
                }}
                sx={{
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: n.status === 'unread' ? 'primary.light' : 'divider',
                  bgcolor: n.status === 'unread' ? 'rgba(26, 56, 255, 0.04)' : 'background.paper',
                  transition: 'box-shadow 0.2s ease',
                  cursor: n.status === 'unread' ? 'pointer' : 'default',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                }}
              >
                <Box sx={{ mt: 0.5 }}>{getIcon(n.category)}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: n.status === 'unread' ? 600 : 500 }}>
                    {n.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {n.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon sx={{ fontSize: 14 }} />
                    {n.time_ago || formatTime(n.created_at)}
                    {n.client_name && ` • ${n.client_name}`}
                  </Typography>
                </Box>
                {n.status === 'unread' && (
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(n.id);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                  >
                    Mark read
                  </Button>
                )}
              </Paper>
            ))}
          </Box>
        )}
    </PageLayout>
  );
};

export default AdminNotificationsPage;
